import React, { useState, useEffect, useContext } from "react";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import {
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaBug,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { getAuth } from "firebase/auth";
import { AuthContext } from "../../AuthProvider";

// TODO – replace with dynamic value (e.g. user.defaultTeamId)
const TEAM_ID = "zTS5IWYUGpICLRKioMPm";

// helper – normalise to Firestore-safe docId (same as importer script)
const sanitiseEmail = (email) => {
  // The documents in Firestore appear to be stored with the raw email as ID
  // (keeping @ symbols and other characters intact)
  const sanitized = email.trim().toLowerCase();
  return sanitized;
};

const BlacklistChecker = () => {
  const [emailsText, setEmails] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [authReady, setAuthReady] = useState(false);
  const [loggingEnabled, setLoggingEnabled] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(true);

  // Use AuthContext instead of direct Firebase auth
  const { user, isAuthChecking } = useContext(AuthContext);

  // Conditional logging helper
  const debugLog = (...args) => {
    if (loggingEnabled) {
      console.log(...args);
    }
  };

  const debugError = (...args) => {
    if (loggingEnabled) {
      console.error(...args);
    }
  };

  const debugLogWithEmail = (email, sanitized) => {
    if (loggingEnabled) {
      console.log(`DEBUG - Sanitizing: "${email}" → "${sanitized}"`);
    }
  };

  // Fetch teams where user is owner/creator/reader (similar to TeamSettings.js)
  useEffect(() => {
    const fetchUserTeams = async () => {
      if (!user) {
        setTeamsLoading(false);
        return;
      }

      try {
        setTeamsLoading(true);
        debugLog("DEBUG - Fetching user teams...");

        const teamsRef = collection(db, "teams");
        const ownerQ = query(
          teamsRef,
          where("owners", "array-contains", user.uid)
        );
        const creatorQ = query(
          teamsRef,
          where("creators", "array-contains", user.email)
        );
        const readerQ = query(
          teamsRef,
          where("readers", "array-contains", user.email)
        );

        const [ownSnap, creSnap, readSnap] = await Promise.all([
          getDocs(ownerQ),
          getDocs(creatorQ),
          getDocs(readerQ),
        ]);

        const map = new Map();
        ownSnap.forEach((d) =>
          map.set(d.id, { id: d.id, ...d.data(), role: "owner" })
        );
        creSnap.forEach((d) => {
          if (!map.has(d.id))
            map.set(d.id, { id: d.id, ...d.data(), role: "creator" });
        });
        readSnap.forEach((d) => {
          if (!map.has(d.id))
            map.set(d.id, { id: d.id, ...d.data(), role: "reader" });
        });

        const userTeams = Array.from(map.values());
        setTeams(userTeams);
        debugLog("DEBUG - Found teams:", userTeams);

        // Auto-select if only one team
        if (userTeams.length === 1) {
          setSelectedTeamId(userTeams[0].id);
          setSelectedTeam(userTeams[0]);
          debugLog("DEBUG - Auto-selected single team:", userTeams[0]);
        }
      } catch (err) {
        debugError("DEBUG - Error fetching teams:", err);
        toast.error("Failed to load teams");
      } finally {
        setTeamsLoading(false);
      }
    };

    if (!isAuthChecking && user) {
      fetchUserTeams();
    } else if (!isAuthChecking && !user) {
      setTeamsLoading(false);
    }
  }, [user, isAuthChecking, loggingEnabled]);

  // Debug: Check current user and team data
  useEffect(() => {
    // Wait until authentication check is complete
    if (isAuthChecking) {
      debugLog("DEBUG - Auth check still in progress...");
      return;
    }

    setAuthReady(true);

    const fetchDebugInfo = async () => {
      if (!selectedTeamId || !user) return;

      try {
        debugLog("DEBUG - Auth state from context:", !!user);
        debugLog(
          "DEBUG - User from context:",
          user ? { uid: user.uid, email: user.email } : null
        );

        if (!user) {
          debugLog("DEBUG - No authenticated user found!");
          setDebugInfo({
            user: null,
            authStatus: "Not authenticated",
            message: "You must be logged in to use this feature",
          });
          return;
        }

        // Add more detailed email debug info
        debugLog(
          "DEBUG - User email (client-side equivalent of request.auth.token.email):",
          user.email
        );

        // Check team document
        const teamRef = doc(db, "teams", selectedTeamId);
        const teamDoc = await getDoc(teamRef);

        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          debugLog("DEBUG - Team exists:", true);
          debugLog("DEBUG - Team data:", {
            owners: teamData.owners || [],
            creators: teamData.creators || [],
            readers: teamData.readers || [],
          });

          // Check if current user has permissions
          const userHasOwnerAccess =
            user && teamData.owners && teamData.owners.includes(user.uid);
          const userHasCreatorAccess =
            user && teamData.creators && teamData.creators.includes(user.email);
          const userHasReaderAccess =
            user && teamData.readers && teamData.readers.includes(user.email);

          debugLog("DEBUG - Access checks:", {
            userHasOwnerAccess,
            userHasCreatorAccess,
            userHasReaderAccess,
            hasAnyAccess:
              userHasOwnerAccess || userHasCreatorAccess || userHasReaderAccess,
          });

          setDebugInfo({
            user: user
              ? {
                  uid: user.uid,
                  email: user.email,
                  emailNote:
                    "This client-side user.email is what becomes request.auth.token.email in Firestore rules",
                }
              : null,
            authStatus: "Authenticated",
            team: {
              id: selectedTeamId,
              exists: true,
              access: {
                isOwner: userHasOwnerAccess,
                isCreator: userHasCreatorAccess,
                isReader: userHasReaderAccess,
                hasAnyAccess:
                  userHasOwnerAccess ||
                  userHasCreatorAccess ||
                  userHasReaderAccess,
              },
            },
          });
        } else {
          debugLog("DEBUG - Team doesn't exist!");
          setDebugInfo({
            user: user
              ? {
                  uid: user.uid,
                  email: user.email,
                  emailNote:
                    "This client-side user.email is what becomes request.auth.token.email in Firestore rules",
                }
              : null,
            authStatus: "Authenticated",
            team: { id: selectedTeamId, exists: false },
          });
        }
      } catch (err) {
        debugError("DEBUG - Error fetching debug info:", err);
        setDebugInfo({
          user: user
            ? {
                uid: user.uid,
                email: user.email,
                emailNote:
                  "This client-side user.email is what becomes request.auth.token.email in Firestore rules",
              }
            : null,
          authStatus: user ? "Authenticated" : "Not authenticated",
          error: err.message,
        });
      }
    };

    fetchDebugInfo();
  }, [user, isAuthChecking, loggingEnabled, selectedTeamId]);

  // read from Firestore
  const checkEmails = async () => {
    // Ensure authentication is complete
    if (!user) {
      return toast.error("You must be logged in to use this feature");
    }

    if (!selectedTeamId) {
      return toast.error("Please select a team first");
    }

    let list = emailsText
      .split(/[\s,]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (list.length === 0) {
      return toast.warn("Enter at least one e-mail");
    }
    if (list.length > 50) {
      return toast.error("Max 50 emails at once");
    }

    // Enhanced debugging - check what Firebase Auth actually provides
    debugLog("=== ENHANCED AUTH DEBUGGING ===");
    debugLog("DEBUG - Client user object:", {
      uid: user.uid,
      email: user.email,
      emailVerified: user.emailVerified,
      displayName: user.displayName,
    });

    // Get the actual ID token to see what claims are included
    try {
      const idToken = await user.getIdToken(false);
      debugLog("DEBUG - ID Token exists:", !!idToken);

      // Decode the token to see what's actually in it (this is what Firestore rules see)
      const tokenResult = await user.getIdTokenResult(false);
      debugLog("DEBUG - Token claims:", {
        uid: tokenResult.claims.sub,
        email: tokenResult.claims.email,
        email_verified: tokenResult.claims.email_verified,
        auth_time: tokenResult.claims.auth_time,
        firebase: tokenResult.claims.firebase,
      });
      debugLog("DEBUG - Full token claims object:", tokenResult.claims);
    } catch (tokenErr) {
      debugError("DEBUG - Error getting token:", tokenErr);
    }

    // Double-check auth state right before making the request
    debugLog("DEBUG - Auth state at request time (from context):", !!user);
    debugLog(
      "DEBUG - User at request time:",
      user
        ? {
            uid: user.uid,
            email: user.email,
            emailNote:
              "This client-side user.email is what Firestore sees as request.auth.token.email",
          }
        : null
    );

    // Try to get the team document again to verify permissions at request time
    try {
      const teamRef = doc(db, "teams", selectedTeamId);
      const teamDoc = await getDoc(teamRef);
      debugLog("DEBUG - Team access at request time:", teamDoc.exists());

      if (teamDoc.exists()) {
        const teamData = teamDoc.data();
        debugLog("=== DETAILED TEAM DATA ===");
        debugLog("DEBUG - Team owners array:", teamData.owners);
        debugLog("DEBUG - Team creators array:", teamData.creators);
        debugLog("DEBUG - Team readers array:", teamData.readers);
        debugLog("DEBUG - Team userIds array:", teamData.userIds);

        // Manual permission checks with exact matching
        debugLog("=== CLIENT-SIDE PERMISSION CHECKS ===");
        debugLog("DEBUG - user.uid:", user.uid);
        debugLog(
          "DEBUG - owners.includes(user.uid):",
          teamData.owners?.includes(user.uid)
        );
        debugLog("DEBUG - user.email:", user.email);
        debugLog(
          "DEBUG - creators.includes(user.email):",
          teamData.creators?.includes(user.email)
        );
        debugLog(
          "DEBUG - readers.includes(user.email):",
          teamData.readers?.includes(user.email)
        );
        debugLog(
          "DEBUG - userIds.includes(user.uid):",
          teamData.userIds?.includes(user.uid)
        );

        // Check for case sensitivity or whitespace issues
        debugLog("=== EMAIL COMPARISON DEBUGGING ===");
        if (teamData.creators) {
          teamData.creators.forEach((email, index) => {
            debugLog(
              `DEBUG - creators[${index}]: "${email}" (length: ${email.length})`
            );
            debugLog(
              `DEBUG - user.email: "${user.email}" (length: ${user.email.length})`
            );
            debugLog(`DEBUG - Exact match: ${email === user.email}`);
            debugLog(
              `DEBUG - Case insensitive match: ${
                email.toLowerCase() === user.email.toLowerCase()
              }`
            );
          });
        }
        if (teamData.readers) {
          teamData.readers.forEach((email, index) => {
            debugLog(
              `DEBUG - readers[${index}]: "${email}" (length: ${email.length})`
            );
            debugLog(
              `DEBUG - user.email: "${user.email}" (length: ${user.email.length})`
            );
            debugLog(`DEBUG - Exact match: ${email === user.email}`);
            debugLog(
              `DEBUG - Case insensitive match: ${
                email.toLowerCase() === user.email.toLowerCase()
              }`
            );
          });
        }

        // Simulate the security rule evaluation
        debugLog("=== SIMULATING SECURITY RULE EVALUATION ===");
        const hasOwnerAccess = teamData.owners?.includes(user.uid);
        const hasUserIdAccess = teamData.userIds?.includes(user.uid);
        const hasCreatorAccess = teamData.creators?.includes(user.email);
        const hasReaderAccess = teamData.readers?.includes(user.email);

        debugLog("DEBUG - Rule evaluation simulation:");
        debugLog("  - _has(t.data.owners, request.auth.uid):", hasOwnerAccess);
        debugLog(
          "  - _has(t.data.userIds, request.auth.uid):",
          hasUserIdAccess
        );
        debugLog(
          "  - _has(t.data.creators, request.auth.token.email):",
          hasCreatorAccess
        );
        debugLog(
          "  - _has(t.data.readers, request.auth.token.email):",
          hasReaderAccess
        );
        debugLog(
          "  - canSeeTeamDocs result:",
          hasOwnerAccess ||
            hasUserIdAccess ||
            hasCreatorAccess ||
            hasReaderAccess
        );
      }
    } catch (err) {
      debugError("DEBUG - Error checking team at request time:", err);
    }

    setLoading(true);
    try {
      const out = [];
      // simple sequential fetch ( <50 docs – fine for UX )
      for (const raw of list) {
        const id = sanitiseEmail(raw);
        const ref = doc(db, "teams", selectedTeamId, "blacklist", id);

        debugLogWithEmail(raw, id);
        debugLog(
          `DEBUG - Document path: "teams/${selectedTeamId}/blacklist/${id}"`
        );
        debugLog(`DEBUG - Auth state at fetch time:`, !!user);
        debugLog(`DEBUG - User performing lookup:`, {
          uid: user.uid,
          email: user.email,
          emailForRules: `Same as request.auth.token.email in rules`,
        });

        // Add security rules debugging info
        debugLog(
          `DEBUG - Security rules checks that should be true for success:`
        );
        debugLog(
          `- canSeeTeamDocs(${selectedTeamId}) = isTeamOwner || isTeamCreatorByEmail || isTeamReaderByEmail`
        );
        debugLog(
          `- From debug info, isOwner=${debugInfo?.team?.access?.isOwner}, isCreator=${debugInfo?.team?.access?.isCreator}`
        );

        try {
          // Try with the standard reference format first
          debugLog(
            `DEBUG - Attempting with standard reference format: doc(db, "teams", selectedTeamId, "blacklist", id)`
          );
          try {
            const snap = await getDoc(ref);
            debugLog(`DEBUG - Success! Document exists: ${snap.exists()}`);
            out.push({
              email: raw,
              found: snap.exists(),
            });
            continue; // Skip alternative method if this one worked
          } catch (err) {
            debugError(
              `DEBUG - Standard reference format failed:`,
              err.message
            );
            debugError(`DEBUG - Error code:`, err.code);
            debugError(`DEBUG - Full error:`, err);
            // Continue to try alternative format
          }

          // Try with string path format as fallback
          debugLog(
            `DEBUG - Attempting with string path format: doc(db, \`teams/${selectedTeamId}/blacklist/${id}\`)`
          );
          const directRef = doc(db, `teams/${selectedTeamId}/blacklist/${id}`);
          try {
            const directSnap = await getDoc(directRef);
            debugLog(
              `DEBUG - Success with string path! Document exists: ${directSnap.exists()}`
            );
            out.push({
              email: raw,
              found: directSnap.exists(),
            });
          } catch (directErr) {
            debugError(
              `DEBUG - String path format also failed:`,
              directErr.message
            );
            throw directErr; // Re-throw to be caught by outer catch
          }
        } catch (err) {
          debugError(`DEBUG - All attempts failed for "${id}":`, err);
          debugError(`DEBUG - Error type:`, err.constructor.name);
          debugError(`DEBUG - Error code:`, err.code);

          // Enhanced team document verification
          try {
            debugLog(
              "DEBUG - Trying to read team document directly to verify permissions"
            );
            const teamRef = doc(db, "teams", selectedTeamId);
            const teamDoc = await getDoc(teamRef);
            debugLog("DEBUG - Team doc access:", teamDoc.exists());
            if (teamDoc.exists()) {
              const data = teamDoc.data();
              debugLog("DEBUG - Team owners:", data.owners);
              debugLog("DEBUG - Team creators:", data.creators);
              debugLog("DEBUG - Team readers:", data.readers);
              debugLog("DEBUG - Team userIds:", data.userIds);

              // Test if we can read any subcollection
              debugLog("DEBUG - Testing access to other subcollections...");

              // Try to read a generator document
              try {
                const generatorRef = doc(
                  db,
                  "teams",
                  selectedTeamId,
                  "generator",
                  "test"
                );
                const generatorDoc = await getDoc(generatorRef);
                debugLog(
                  "DEBUG - Generator subcollection access:",
                  "Success (document may not exist but no permission error)"
                );
              } catch (genErr) {
                debugError(
                  "DEBUG - Generator subcollection access failed:",
                  genErr.message
                );
              }

              // Try to read a settings document
              try {
                const settingsRef = doc(
                  db,
                  "teams",
                  selectedTeamId,
                  "settings",
                  "test"
                );
                const settingsDoc = await getDoc(settingsRef);
                debugLog(
                  "DEBUG - Settings subcollection access:",
                  "Success (document may not exist but no permission error)"
                );
              } catch (setErr) {
                debugError(
                  "DEBUG - Settings subcollection access failed:",
                  setErr.message
                );
              }

              // DEBUG: Test if the issue is with the canSeeTeamDocs function
              debugLog("=== TESTING RULE COMPONENTS ===");
              debugLog("DEBUG - Current user should have access because:");
              debugLog(
                `  - User UID ${user.uid} is in owners: ${data.owners?.includes(
                  user.uid
                )}`
              );
              debugLog(
                `  - User email ${
                  user.email
                } is in creators: ${data.creators?.includes(user.email)}`
              );
              debugLog(
                "DEBUG - If blacklist still fails, the issue is likely:"
              );
              debugLog("  1. Multiple get() calls in security rules");
              debugLog("  2. _has() function implementation");
              debugLog("  3. _team() function caching/performance");

              // Try to understand why generator works but blacklist doesn't
              debugLog("=== RULE COMPARISON ===");
              debugLog(
                "Generator rule: allow read: if request.auth != null; (works)"
              );
              debugLog(
                "Blacklist rule: allow read: if canSeeTeamDocs(teamId); (fails)"
              );
              debugLog(
                "This suggests the issue is in canSeeTeamDocs() function"
              );
            }
          } catch (teamErr) {
            debugError("DEBUG - Team doc access error:", teamErr.message);
          }

          out.push({
            email: raw,
            error: err.message,
          });
        }
      }
      setResults(out);
    } catch (err) {
      debugError("DEBUG - Global error:", err);
      toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while waiting for auth to be ready
  if (isAuthChecking || teamsLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Blacklist Checker</h1>
        <div className="p-4 bg-blue-50 text-blue-700 rounded">
          Loading authentication state...
        </div>
      </div>
    );
  }

  // Show login required message if not authenticated
  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Blacklist Checker</h1>
        <div className="p-4 bg-yellow-50 text-yellow-700 rounded">
          You must be logged in to use this feature.
        </div>
      </div>
    );
  }

  // Show no teams message
  if (teams.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Blacklist Checker</h1>

          {/* Debug Logging Toggle */}
          <div className="flex items-center space-x-2">
            <FaBug
              className={`${
                loggingEnabled ? "text-green-600" : "text-gray-400"
              }`}
            />
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={loggingEnabled}
                onChange={(e) => setLoggingEnabled(e.target.checked)}
                className="form-checkbox h-4 w-4 text-purple-600"
              />
              <span className="text-sm text-gray-700">Debug Logging</span>
            </label>
          </div>
        </div>

        <div className="text-center py-10 bg-gray-50 rounded-lg">
          <FaUsers className="mx-auto text-4xl text-gray-400 mb-3" />
          <p className="text-gray-600 mb-4">
            Not part of any team - please contact admin for help
          </p>
        </div>
      </div>
    );
  }

  // Show team selector if multiple teams and none selected
  if (teams.length > 1 && !selectedTeamId) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Blacklist Checker</h1>

          {/* Debug Logging Toggle */}
          <div className="flex items-center space-x-2">
            <FaBug
              className={`${
                loggingEnabled ? "text-green-600" : "text-gray-400"
              }`}
            />
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={loggingEnabled}
                onChange={(e) => setLoggingEnabled(e.target.checked)}
                className="form-checkbox h-4 w-4 text-purple-600"
              />
              <span className="text-sm text-gray-700">Debug Logging</span>
            </label>
          </div>
        </div>

        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Select a Team</h3>
          <div className="space-y-3">
            {teams.map((team) => (
              <div
                key={team.id}
                className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setSelectedTeamId(team.id);
                  setSelectedTeam(team);
                }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-lg">{team.name}</h4>
                    <p className="text-sm text-gray-600">
                      {team.role === "owner"
                        ? "Team Owner"
                        : team.role === "creator"
                        ? "Creator Access"
                        : "Reader Access"}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    {team.createdAt?.toDate
                      ? new Date(team.createdAt.toDate()).toLocaleDateString()
                      : team.createdAt instanceof Date
                      ? team.createdAt.toLocaleDateString()
                      : "Date unknown"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold">Blacklist Checker</h1>
          {selectedTeam && (
            <span className="ml-3 text-sm text-gray-600">
              Team: <span className="font-medium">{selectedTeam.name}</span>
            </span>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Team selector for multiple teams */}
          {teams.length > 1 && (
            <button
              onClick={() => {
                setSelectedTeamId(null);
                setSelectedTeam(null);
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Change Team
            </button>
          )}

          {/* Debug Logging Toggle */}
          <div className="flex items-center space-x-2">
            <FaBug
              className={`${
                loggingEnabled ? "text-green-600" : "text-gray-400"
              }`}
            />
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={loggingEnabled}
                onChange={(e) => setLoggingEnabled(e.target.checked)}
                className="form-checkbox h-4 w-4 text-purple-600"
              />
              <span className="text-sm text-gray-700">Debug Logging</span>
            </label>
          </div>
        </div>
      </div>

      {/* Text-area input */}
      <textarea
        value={emailsText}
        onChange={(e) => setEmails(e.target.value)}
        placeholder="Paste up to 50 email addresses – one per line, comma or space-separated"
        rows={6}
        className="w-full p-3 border rounded focus:ring-2 focus:ring-purple-500 mb-4 resize-y"
      />

      <button
        onClick={checkEmails}
        disabled={loading || !authReady || !selectedTeamId}
        className="bg-purple-600 text-white px-6 py-2 rounded flex items-center hover:bg-purple-700 disabled:opacity-50"
      >
        <FaSearch className="mr-2" />
        {loading ? "Checking…" : "Check Emails"}
      </button>

      {/* Results */}
      {results.length > 0 && (
        <div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {results.map(({ email, found, error }) => (
                <tr
                  key={email}
                  className={
                    error ? "bg-yellow-50" : found ? "bg-red-50" : "bg-green-50"
                  }
                >
                  <td className="px-4 py-3 font-mono break-all">{email}</td>
                  <td className="px-4 py-3 flex items-center space-x-2">
                    {error ? (
                      <>
                        <FaTimesCircle className="text-yellow-600" />
                        <span className="text-yellow-600 font-semibold">
                          Error: {error}
                        </span>
                      </>
                    ) : found ? (
                      <>
                        <FaTimesCircle className="text-red-600" />
                        <span className="text-red-600 font-semibold">
                          Black-listed – do NOT use
                        </span>
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="text-green-600" />
                        <span className="text-green-600 font-semibold">
                          Safe to use
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlacklistChecker;
